/**
 * @file     emailLinkOrigin.ts
 * @purpose  Resolve the origin embedded in emailed auth links. Emails outlive
 *           the request that minted them, so localhost / preview-deploy hosts
 *           must never leak into an inbox: in production the canonical site
 *           origin always wins over whatever Host the request arrived on.
 */
const CANONICAL_ORIGIN = 'https://www.relta.ca';
const TRUSTED_ORIGINS = new Set([CANONICAL_ORIGIN, 'https://relta.ca']);

export function resolveEmailLinkOrigin(requestOrigin: string | null | undefined): string {
  if (process.env.NODE_ENV !== 'production') {
    return requestOrigin || CANONICAL_ORIGIN;
  }
  if (requestOrigin && TRUSTED_ORIGINS.has(requestOrigin)) return requestOrigin;
  return process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '') || CANONICAL_ORIGIN;
}

const PROBE_ORIGIN = 'https://next-path.invalid';

/**
 * Same-origin relative path guard for `next=` values that end up inside
 * emailed links. Rejects protocol-relative (`//evil.com`) and backslash tricks.
 * URL parsing strips tab/newline, so `/\t/evil.com` would otherwise resolve to
 * `//evil.com`: control characters are refused outright, and the value must
 * still resolve to the same origin.
 */
export function safeNextPath(value: unknown, fallback = '/dashboard'): string {
  if (typeof value !== 'string') return fallback;
  if (!value.startsWith('/') || value.startsWith('//') || /[\u0000-\u001F\u007F\\]/.test(value)) return fallback;
  if (new URL(value, PROBE_ORIGIN).origin !== PROBE_ORIGIN) return fallback;
  return value;
}
